/**
 * Opportunities Page
 * Displays all government contracting opportunities with search and filtering.
 * Rendered inside OpportunitiesLayout which provides tab navigation.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageBody, PageHero, PageToolbar } from '../../components/layout';
import { useDebounce } from '../../hooks/useDebounce';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { NewOpportunityForm } from './components/NewOpportunityForm';
import { OpportunityContextEntry } from './components/OpportunityContext';
import { OpportunityQualificationEntry, OpportunityQualificationWorkspace } from './components/OpportunityDetailModal';
import { OpportunityFilters } from './components/OpportunityFilters';
import { OpportunityList } from './components/OpportunityList';
import { QuickFilters } from './components/QuickFilters';
import { useCanonicalOpportunities } from './hooks/useCanonicalOpportunities';
import { useMarketDeskParams } from './hooks/useMarketDeskParams';
import { AwardSignalsEntry } from './Signals/AwardSignals';
import { OpportunityWatchEntry } from './Watch/OpportunityWatchEntry';
const SEARCH_DEBOUNCE_MS = 300;
export function OpportunitiesPage() {
    // Search, filters, view, page and the open Notice live in the URL so Back,
    // refresh and shared links restore the same market desk (SAD-467).
    const desk = useMarketDeskParams();
    const [params] = useSearchParams();
    const qualification = params.get('qualification');
    const canonical = useCanonicalOpportunities();
    const selectedOpportunity = canonical.data?.find((o)=>o.id === params.get('watchOpportunity'));
    const { filters, quickFilters, viewMode } = desk;
    const [searchInput, setSearchInput] = useState(desk.search);
    const [totalCount, setTotalCount] = useState(0);
    const [filteredCount, setFilteredCount] = useState(undefined);
    // The input stays local so typing is instant; the debounced value is written
    // to the URL with replace so keystrokes don't pile up history entries.
    const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
    const lastWritten = useRef(desk.search);
    const { setSearch } = desk;
    useEffect(()=>{
        if (debouncedSearch === lastWritten.current) return;
        lastWritten.current = debouncedSearch;
        setSearch(debouncedSearch);
    }, [
        debouncedSearch,
        setSearch
    ]);
    // URL changed from outside (Back/Forward, clear): reflect it in the input.
    useEffect(()=>{
        if (desk.search === lastWritten.current) return;
        lastWritten.current = desk.search;
        setSearchInput(desk.search);
    }, [
        desk.search
    ]);
    // PageHero is the inline (Specra) header, so hide the legacy global top-bar
    // header and the breadcrumb bar while this page is mounted.
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader
    ]);
    // Count active advanced (dialog) filters so the quick bar can surface and
    // clear them alongside the chips. Exclude the dialog's setAsideType when a
    // quick set-aside chip is active — the chip wins (see OpportunityList), so
    // the dialog value isn't actually applied and shouldn't inflate the count.
    const dialogFilterCount = Object.entries(filters).filter(([key, v])=>{
        if (v === undefined || v === '') return false;
        if (key === 'setAsideType' && quickFilters.setAside !== null) return false;
        return true;
    }).length;
    const clearDialogFilters = ()=>desk.setFilters({});
    if (qualification) return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsx(OpportunityQualificationWorkspace, {
            opportunityId: qualification
        })
    });
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.opportunities.page,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Government Opportunities",
                    subtitle: "Browse available contracting opportunities"
                }),
                /*#__PURE__*/ _jsxs(PageToolbar, {
                    children: [
                        /*#__PURE__*/ _jsx(PageToolbar.Search, {
                            value: searchInput,
                            onChange: setSearchInput,
                            placeholder: "Search opportunities by title, description, or agency...",
                            className: "flex-1 max-w-none",
                            dataTestid: Testids.opportunities.searchInput
                        }),
                        /*#__PURE__*/ _jsx(OpportunityFilters, {
                            filters: filters,
                            onFiltersChange: desk.setFilters
                        }),
                        /*#__PURE__*/ _jsx(NewOpportunityForm, {})
                    ]
                }),
                /*#__PURE__*/ _jsx(OpportunityContextEntry, {}),
                /*#__PURE__*/ _jsx(OpportunityWatchEntry, {}),
                !desk.notice && selectedOpportunity && /*#__PURE__*/ _jsx(OpportunityQualificationEntry, {
                    opportunityId: selectedOpportunity.id,
                    title: selectedOpportunity.title
                }),
                /*#__PURE__*/ _jsx(AwardSignalsEntry, {}),
                /*#__PURE__*/ _jsx(QuickFilters, {
                    filters: quickFilters,
                    onFilterChange: desk.setQuickFilters,
                    viewMode: viewMode,
                    onViewModeChange: desk.setViewMode,
                    totalCount: totalCount,
                    filteredCount: filteredCount,
                    dialogFilterCount: dialogFilterCount,
                    onClearAll: desk.clearAll,
                    onClearDialogFilters: clearDialogFilters
                }),
                /*#__PURE__*/ _jsx(OpportunityList, {
                    search: desk.search,
                    filters: filters,
                    quickFilters: quickFilters,
                    viewMode: viewMode,
                    page: desk.page,
                    onPageChange: desk.setPage,
                    noticeId: desk.notice,
                    onNoticeChange: desk.setNotice,
                    onTotalChange: setTotalCount,
                    onFilteredCountChange: setFilteredCount
                })
            ]
        })
    });
}
